import { useState } from "react";
import styled, { css } from "styled-components";
import { useParams } from "react-router-dom";
import { useRecoilState } from "recoil";

import COLORS from "components/atoms/colors";
import SHADOWS from "components/atoms/shadows";
import Library from "components/templates/project/SideBar/Library";
import Search from "components/templates/project/SideBar/Search";
import Toast from "components/templates/project/Toast";
import Crop from "components/templates/project/Scene/Crop";
import SceneImage from "components/templates/project/Scene/Image";
import Modal from "components/organisms/Modal";
import ModalImageGuide from "components/templates/project/SideBar/ModalImageGuide";
import SIDE_BAR_WIDTH from "components/atoms/sideBarWidth";
import Detail from "components/templates/project/SideBar/LiveProduct/Detail";

import useProject from "apis/queries/project/useProject";
import sceneIndexAtom from "stores/sceneIndex";

import SceneImageMode from "types/SceneImgMode";
import SearchType from "types/SearchType";

import { ReactComponent as IconScene } from "assets/images/project/scene.svg";
import { ReactComponent as IconSearch } from "assets/images/project/search.svg";

const TABS = [
  {
    name: "SCENE",
    Icon: IconScene,
  },
  {
    name: "AI",
    Icon: IconSearch,
  },
];

const Content = () => {
  const [tab, setTab] = useState(0);
  const [productOptionId, setProductOptionId] = useState(0);
  const [cropImage, setCropImage] = useState("");
  const [sceneIndex, setSceneIndex] = useRecoilState(sceneIndexAtom);
  const [pinId, setPinId] = useState(0);
  const [productId, setProductId] = useState(0);
  const [sceneImageMode, setSceneImageMode] = useState<SceneImageMode>("none");
  const [cropBoxData, setCropBoxData] = useState<Cropper.CropBoxData | null>(
    null
  );
  const [searchType, setSearchType] = useState<SearchType>("list");

  const params = useParams();
  const projectId = Number(params.id);

  // 프로젝트 상세 api
  const { project } = useProject({ projectId });

  if (!project) return null;

  // 현재 씬 데이터
  const currentScene = project.scenes[sceneIndex];

  // 사이드 영역 탭 클릭 이벤트
  const handleClickTab = ({
    index,
    onOpen,
  }: {
    index: number;
    onOpen: () => void;
  }) => {
    if (index === 1 && project.scenes.length === 0) {
      onOpen();
      return;
    }

    if (index === 0) {
      setSceneImageMode("none");
      setCropBoxData(null);
    }

    // 기획변경 - AI탭 클릭 시 크롭 모드로 변경
    if (index === 1) {
      setSceneImageMode("crop");
    }

    setTab(index);
  };

  // 상품 상세 컴포넌트 오픈 조건
  // 기획변경 - AI탭 클릭 시 크롭 모드로 변경 (코드 위치 변경)
  const isShowDetail = searchType === "detail" || !!pinId;

  // 기획변경 - AI탭 클릭 시 크롭 모드로 변경 (코드 위치 변경)
  const handleCloseDetail = () => {
    setSearchType("list");
    setPinId(0);
  };

  return (
    <>
      <Aside>
        <TabWrapper>
          {TABS.map(({ Icon, name }, index) => {
            return (
              <Modal
                key={index}
                activator={(onOpen) => (
                  <TabItem
                    active={tab === index}
                    onClick={() => handleClickTab({ index, onOpen })}
                  >
                    <Icon />
                    <p>{name}</p>
                  </TabItem>
                )}
                modal={(onClose) => <ModalImageGuide onClose={onClose} />}
              />
            );
          })}
        </TabWrapper>

        {isShowDetail && (
          <Detail
            productId={productId}
            productOptionId={productOptionId}
            pinId={pinId}
            onChangeProductOptionId={(optionId) => setProductOptionId(optionId)}
            onClose={handleCloseDetail}
            onChageSceneModePin={() => setSceneImageMode("pin")}
          />
        )}

        {
          {
            0: <Library onChangeSceneIndex={(i) => setSceneIndex(i)} />,
            1: (
              <Search
                searchType={searchType}
                onSearchType={(type) => setSearchType(type)}
                productOptionId={productOptionId}
                cropImage={cropImage}
                pinId={pinId}
                productId={productId}
                sceneImageMode={sceneImageMode}
                onChangeProductOptionId={(optionId) =>
                  setProductOptionId(optionId)
                }
                onChangeSceneImageMode={(mode) => setSceneImageMode(mode)}
                onResetPin={() => setPinId(0)}
                onChageProductId={(id) => setProductId(id)}
                onResetCropBoxData={() => setCropBoxData(null)}
              />
            ),
          }[tab]
        }
      </Aside>

      <SceneContainer>
        <Toast
          content="샘플을 적용할 구역을 마우스로 클릭해주세요"
          isShow={sceneImageMode === "pin"}
        />

        {sceneImageMode === "crop" ? (
          <Crop
            sceneImg={currentScene.image}
            onCrop={(image) => setCropImage(image)}
            cropBoxData={cropBoxData}
            onCropBoxData={(data) => setCropBoxData(data)}
          />
        ) : (
          <>
            <SceneImage
              productOptionId={productOptionId}
              currentScene={currentScene}
              sceneImageMode={sceneImageMode}
              pinId={pinId}
              productId={productId}
              onClickPin={(params) => {
                setProductId(params.productId);
                // setTab(1);
                setPinId(params.pinId);
              }}
              onChangeCropMode={() => setSceneImageMode("crop")}
              cropBoxData={cropBoxData}
            />
          </>
        )}
        {/* 씬 인디케이터 */}
        {/* {project.scenes.length > 1 && (
          <Indicator>
            <ul className="flex gap-3">
              {project.scenes.map((scene, i) => (
                <IndicatorItem
                  key={scene.id}
                  active={scene.id === currentScene?.id}
                  onClick={() => setSceneIndex(i)}
                ></IndicatorItem>
              ))}
            </ul>
          </Indicator>
        )} */}
      </SceneContainer>
    </>
  );
};

const Aside = styled.aside`
  position: relative;
  flex: none;
  width: ${SIDE_BAR_WIDTH}px;
  height: 100%;
  background: #fff;
  box-shadow: ${SHADOWS.modal};
  z-index: 10;
`;

const SceneContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  background-color: ${COLORS.primary.background};
  overflow: hidden;
`;

const TabWrapper = styled.ul`
  position: absolute;
  top: 0;
  right: -50px;
  z-index: 1;
`;

const TabItem = styled.li<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: ${COLORS.primary.active};
  width: 50px;
  height: 104px;
  border-radius: 0 8px 8px 0px;
  cursor: pointer;

  p {
    margin-top: 4px;
    color: #fff;
    font-weight: 700;
  }

  & > svg path {
    fill: #fff;
  }

  ${({ active }) =>
    active &&
    css`
      background-color: #fff;

      p {
        color: ${COLORS.primary.active};
      }

      & > svg path {
        fill: ${COLORS.primary.active};
      }
    `}
`;

const Indicator = styled.div`
  position: absolute;
  left: 0;
  bottom: 60px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const IndicatorItem = styled.li<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 100%;
  background-color: ${({ active }) =>
    active ? COLORS.primary.active : COLORS.white["60%"]};
  cursor: pointer;
`;

export default Content;
